import { Text } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { CurrencyValue as TCurrencyValue } from '@payaca/types/currencyTypes';
import { FC } from 'react';

import AnimatedHeight from '@/ui/components/estimatesAndProposals/animatedHeight/AnimatedHeight';

type GrantOptionLabelProps = {
  name: string;
  description?: string | null;
  availableAmount: TCurrencyValue;
  appliedAmount?: TCurrencyValue;
};

/**
 * The amount is positive: it is money the provider receives from the scheme.
 * Only the customer's document presents it as a deduction.
 */
const GrantOptionLabel: FC<GrantOptionLabelProps> = (props) => {
  const { name, description, availableAmount, appliedAmount } = props;

  return (
    <AnimatedHeight as="span" className="min-w-0 flex-1">
      <span className="flex flex-col gap-1">
        <span className="flex justify-between gap-3">
          <Text as="span">{name}</Text>
          <Text as="span">
            <CurrencyValue {...(appliedAmount ?? availableAmount)} />
          </Text>
        </span>
        {description && (
          <Text as="span" level="supporting">
            {description}
          </Text>
        )}
        {appliedAmount && (
          <CappedNote
            appliedAmount={appliedAmount}
            availableAmount={availableAmount}
          />
        )}
      </span>
    </AnimatedHeight>
  );
};

/**
 * A grant takes only what the gross has left after the grants before it, so an
 * applied amount below the face value is correct rather than a miscalculation.
 * Nothing else on the option says so, and the gap is the thing a reader is most
 * likely to report as a bug.
 */
const CappedNote: FC<{
  appliedAmount: TCurrencyValue;
  availableAmount: TCurrencyValue;
}> = (props) => {
  const { appliedAmount, availableAmount } = props;

  if (appliedAmount.value >= availableAmount.value) return null;

  return (
    <Text as="span" level="supporting">
      {appliedAmount.value === 0
        ? 'No amount left to deduct'
        : 'Capped at the remaining total'}{' '}
      (face value <CurrencyValue {...availableAmount} />)
    </Text>
  );
};

export default GrantOptionLabel;
